import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { Package, ArrowsClockwise } from "@phosphor-icons/react";

const statusStyle = {
  paid: "border-accent/30 text-accent bg-accent/10",
  pending: "border-primary/30 text-primary bg-primary/10",
  failed: "border-destructive/30 text-destructive bg-destructive/10",
};

export default function Account() {
  const { user } = useAuth();
  const { add } = useCart();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [prodMap, setProdMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
    api.get("/products").then((r) => {
      const m = {};
      r.data.forEach((p) => { m[p.id] = p; });
      setProdMap(m);
    }).catch(() => {});
  }, []);

  const reorder = (order) => {
    let added = 0;
    const skipped = [];
    (order.items || []).forEach((it) => {
      const p = prodMap[it.product_id];
      if (!p || p.active === false || Number(p.stock) <= 0) {
        skipped.push(it.name);
        return;
      }
      add({ id: it.product_id, name: p.name, price: p.price, image_url: p.image_url || "" }, it.quantity);
      added += 1;
    });
    if (added === 0) {
      toast.error("These items are no longer available");
      return;
    }
    if (skipped.length) toast.warning(`Added ${added} item(s). Unavailable: ${skipped.join(", ")}`);
    else toast.success("Items added to your cart");
    nav("/cart");
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-16">
      <div className="flex items-center justify-between mb-12">
        <div>
          <span className="label-mono text-primary">Account</span>
          <h1 className="font-head text-4xl mt-2">Welcome, {user?.name}</h1>
          <p className="text-muted-foreground font-mono text-sm mt-2">{user?.email}</p>
        </div>
      </div>

      <h2 className="font-head text-2xl mb-6">Order History</h2>
      {loading ? (
        <p className="text-muted-foreground font-mono text-sm">Loading orders…</p>
      ) : orders.length === 0 ? (
        <div data-testid="no-orders" className="border border-black/10 bg-surface p-12 text-center">
          <Package size={40} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">You haven't placed any orders yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="border border-black/10 bg-surface p-6">
              <div className="flex flex-wrap justify-between gap-4 mb-4">
                <div>
                  <span className="label-mono text-muted-foreground text-[0.6rem]">Order · {new Date(o.created_at).toLocaleDateString()}</span>
                  <p className="font-mono text-sm mt-1 text-muted-foreground">{o.session_id?.slice(0, 24)}…</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`label-mono px-3 py-1 border ${statusStyle[o.payment_status] || statusStyle.pending}`}>
                    {o.payment_status}
                  </span>
                  <span className="font-mono text-xl text-primary">${o.amount?.toFixed(2)}</span>
                </div>
              </div>
              <div className="border-t border-black/10 pt-4 space-y-2">
                {o.items?.map((it, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{it.quantity} × {it.name}</span>
                    <span className="font-mono">${(it.price * it.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              {o.payment_status === "paid" && (
                <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-1">
                  <p className="label-mono text-accent text-[0.6rem]">Fulfillment: {o.fulfillment_status}</p>
                  {o.tracking_number && (
                    <p data-testid={`tracking-${o.id}`} className="label-mono text-primary text-[0.6rem]">
                      {o.carrier ? `${o.carrier} · ` : ""}Tracking: {o.tracking_number}
                    </p>
                  )}
                </div>
              )}
              <button
                data-testid={`reorder-${o.id}`}
                onClick={() => reorder(o)}
                className="mt-4 flex items-center gap-2 border border-primary/40 text-primary label-mono px-4 py-2 hover:bg-primary/10 transition-colors"
                style={{ transition: "background-color 0.2s" }}
              >
                <ArrowsClockwise size={15} weight="bold" /> Reorder
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
